"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";
import { FiCheck, FiEdit3, FiImage } from "react-icons/fi";
import Image from "next/image";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cx } from "@/lib/tailwind-utils";
import { PlaceholderImage } from "@/types/forms/question";
import { AnnotationOverlay } from "./annotation-overlay";
import { getSignedUrl } from "@/api/auth/gcs";
interface TemplateImageGalleryProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  images: PlaceholderImage[];
  // Names of images already on the note; shown as added and not selectable.
  addedNames: Set<string>;
  // Called without waiting: the note shows the picks straight away.
  onConfirm: (images: PlaceholderImage[]) => void;
}

// Multi-select chooser for the images a note template carries, used when the
// template's attachments question has pick-from-gallery on.
export default function TemplateImageGallery(t0) {
  const $ = _c(41);
  const {
    open,
    onOpenChange,
    images,
    addedNames,
    onConfirm
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Set();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [selectedIds, setSelectedIds] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = new Map();
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [fetchedUrls, setFetchedUrls] = useState(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Set();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const requestedIdsRef = useRef(t3);
  let t4;
  let t5;
  if ($[3] !== open) {
    t4 = () => {
      if (!open) {
        setSelectedIds(new Set());
      }
    };
    t5 = [open];
    $[3] = open;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[6] !== images || $[7] !== open) {
    t6 = () => {
      if (!open) {
        return;
      }
      const missing = images.filter(p => !p.signedUrl && p.gcsPath && !requestedIdsRef.current.has(p.id));
      if (missing.length === 0) {
        return;
      }
      for (const p_0 of missing) {
        requestedIdsRef.current.add(p_0.id);
      }
      Promise.all(missing.map(_temp2)).then(results => {
        if (!results.some(_temp3)) {
          return;
        }
        setFetchedUrls(prev => {
          const next = new Map(prev);
          for (const [id, url_0] of results) {
            if (url_0) {
              next.set(id, url_0);
            }
          }
          return next;
        });
      });
    };
    t7 = [open, images];
    $[6] = images;
    $[7] = open;
    $[8] = t6;
    $[9] = t7;
  } else {
    t6 = $[8];
    t7 = $[9];
  }
  useEffect(t6, t7);
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = id_0 => {
      setSelectedIds(prev_0 => {
        const next_0 = new Set(prev_0);
        if (next_0.has(id_0)) {
          next_0.delete(id_0);
        } else {
          next_0.add(id_0);
        }
        return next_0;
      });
    };
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const toggle = t8;
  let t9;
  if ($[11] !== fetchedUrls || $[12] !== images || $[13] !== onConfirm || $[14] !== onOpenChange || $[15] !== selectedIds) {
    t9 = () => {
      const selected = images.filter(p_2 => selectedIds.has(p_2.id)).map(p_3 => ({
        ...p_3,
        signedUrl: p_3.signedUrl || fetchedUrls.get(p_3.id)
      }));
      if (selected.length === 0) {
        return;
      }
      onConfirm(selected);
      onOpenChange(false);
    };
    $[11] = fetchedUrls;
    $[12] = images;
    $[13] = onConfirm;
    $[14] = onOpenChange;
    $[15] = selectedIds;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  const handleConfirm = t9;
  let t10;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DialogHeader><DialogTitle>Add template images</DialogTitle><DialogDescription>Pick the images you want on this note. Marks saved on a template image come with it, and you can change them after.</DialogDescription></DialogHeader>;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== addedNames || $[19] !== fetchedUrls || $[20] !== images || $[21] !== selectedIds) {
    let t12;
    if ($[23] !== addedNames || $[24] !== fetchedUrls || $[25] !== selectedIds) {
      t12 = image => {
        const isAdded = addedNames.has(image.name);
        const isSelected = selectedIds.has(image.id);
        const url_1 = image.signedUrl || fetchedUrls.get(image.id);
        const markCount = image.annotations?.length ?? 0;
        return <button key={image.id} type="button" disabled={isAdded} onClick={() => toggle(image.id)} aria-pressed={isSelected} className={cx("group flex flex-col gap-1.5 rounded-lg text-left outline-none", isAdded && "cursor-not-allowed opacity-50")}><div className={cx("relative aspect-square w-full overflow-hidden rounded-lg border-2 bg-muted transition-all", "group-focus-visible:ring-2 group-focus-visible:ring-ring group-focus-visible:ring-offset-2 group-focus-visible:ring-offset-background", isSelected ? "border-primary" : "border-transparent md:hover:border-primary/50")}>{url_1 ? <><Image src={url_1} alt={image.name} fill={true} className="object-cover" unoptimized={true} /><AnnotationOverlay annotations={image.annotations ?? []} imageUrl={url_1} /></> : <div className="flex h-full w-full items-center justify-center"><FiImage className="h-8 w-8 text-foreground-muted" /></div>}{markCount > 0 && <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1 rounded-full bg-background/90 px-2 py-0.5 text-caption text-foreground"><FiEdit3 className="h-3 w-3" /><span>{markCount === 1 ? "1 mark" : `${markCount} marks`}</span></div>}{isSelected && <div className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground"><FiCheck className="h-4 w-4" /></div>}</div><span className="truncate text-caption text-foreground-secondary">{isAdded ? `${image.name} (added)` : image.name}</span></button>;
      };
      $[23] = addedNames;
      $[24] = fetchedUrls;
      $[25] = selectedIds;
      $[26] = t12;
    } else {
      t12 = $[26];
    }
    t11 = images.map(t12);
    $[18] = addedNames;
    $[19] = fetchedUrls;
    $[20] = images;
    $[21] = selectedIds;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[27] !== t11) {
    t12 = <DialogBody className="max-h-[60vh] overflow-y-auto"><div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{t11}</div></DialogBody>;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <DialogClose asChild={true}><Button variant="secondary">Cancel</Button></DialogClose>;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  const t14 = selectedIds.size === 0;
  const t15 = selectedIds.size > 1 ? `Add ${selectedIds.size} images` : "Add image";
  let t16;
  if ($[30] !== handleConfirm || $[31] !== t14 || $[32] !== t15) {
    t16 = <DialogFooter>{t13}<Button variant="primary" onClick={handleConfirm} disabled={t14}>{t15}</Button></DialogFooter>;
    $[30] = handleConfirm;
    $[31] = t14;
    $[32] = t15;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t12 || $[35] !== t16) {
    t17 = <DialogContent className="max-w-3xl">{t10}{t12}{t16}</DialogContent>;
    $[34] = t12;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== onOpenChange || $[38] !== open || $[39] !== t17) {
    t18 = <Dialog open={open} onOpenChange={onOpenChange}>{t17}</Dialog>;
    $[37] = onOpenChange;
    $[38] = open;
    $[39] = t17;
    $[40] = t18;
  } else {
    t18 = $[40];
  }
  return t18;
}
function _temp3(t0) {
  const [, url] = t0;
  return url;
}
async function _temp2(p_1) {
  const response = await getSignedUrl(p_1.gcsPath).catch(_temp);
  return [p_1.id, response?.signedUrl] as const;
}
function _temp() {
  return null;
}